import { state } from './state.js';
import * as API from './api.js';
import * as JobManager from './job_manager.js';
import { resetBuildToast } from './job_progress_ui.js';
import { UI_COPY } from './config/uiCopy.js';
import {
    hasSelectedLibraryWithTiles,
    isWorkflowTransitionLocked,
    openCreateMosaicForEdit,
    refreshSettingsVisibility,
    requestWorkflowScroll,
} from './workflow_selection.js';
import { prepareLoadingState } from './result_viewer.js';
import { showToast } from './ui/toast.js';
import { openConfirmModal } from './modal_manager.js';
import { refreshUserConfig } from './auth.js';
import {
    getSelectedMosaicLayout,
    getSimpleDetailExtremeEdge,
    isAdminControlsEnabled,
    isLegacyHighresDraftEnabled,
} from './settings_mode.js';

let hasTrackedGenerationAttempt = false;

function clamp(value, min, max) {
    const numeric = Number(value);
    if (!Number.isFinite(numeric)) return min;
    if (numeric < min) return min;
    if (numeric > max) return max;
    return numeric;
}

function mapUiReusePenaltyToEngine(value) {
    const uiValue = clamp(value, 0, 100);
    const normalized = uiValue / 100;
    // Non-linear mapping spreads visible change more evenly across the slider.
    return Math.round((normalized * normalized) * 1000);
}

function collectJobSettings() {
    const colorModeEl = document.getElementById('colorMode');
    const scaleInput = document.getElementById('scaleInput');
    const reuseInput = document.getElementById('rpInput');
    const colorShareInput = document.getElementById('colorShare');
    const noiseInput = document.getElementById('noiseFactor');
    const adminControlsEnabled = isAdminControlsEnabled();
    const legacyHighresDraft = adminControlsEnabled && isLegacyHighresDraftEnabled();
    const selectedLayout = adminControlsEnabled ? getSelectedMosaicLayout() : 'IRREGULAR';
    const adaptiveSquareMinSizeInput = document.getElementById('adaptiveSquareMinSizeInput');
    const matcherBackendSelect = document.getElementById('matcherBackendSelect');
    const adaptiveSquaresEnabled = selectedLayout === 'QUADTREE';
    const qualityToggle = document.getElementById('qualityToggle');
    const useQualityBuild = adaptiveSquaresEnabled || !adminControlsEnabled || !qualityToggle || Boolean(qualityToggle.checked);
    const uiReusePenalty = clamp(reuseInput ? reuseInput.value : 45, 0, 100);
    const uiColorShare = clamp(colorShareInput ? colorShareInput.value : 50, 0, 100);
    const uiNoiseFactor = clamp(noiseInput ? noiseInput.value : 0, 0, 100);
    const payload = {
        target_filename: state.targetFilename,
        source_photo_id: state.selectedSourcePhotoId || '',
        library_id: state.selectedLibraryId,
        mode: selectedLayout,
        build_mode: useQualityBuild ? 'quality' : 'fast',
        use_legacy_highres_draft: legacyHighresDraft,
        base_tile_height: document.getElementById('thInput').value,
        // Keep backend units unchanged: reuse 0-1000, share/noise 0-1.
        reuse_penalty: String(mapUiReusePenaltyToEngine(uiReusePenalty)),
        color_share: String(uiColorShare / 100),
        noise_factor: String(uiNoiseFactor / 100),
        matcher_backend: adminControlsEnabled && matcherBackendSelect ? String(matcherBackendSelect.value || 'auto') : 'auto',
        color_mode: colorModeEl ? colorModeEl.value : 'smooth',
        allow_flips: document.getElementById('allowFlips') ? document.getElementById('allowFlips').checked : true
    };
    if (adaptiveSquaresEnabled && adaptiveSquareMinSizeInput) {
        payload.adaptive_square_min_size_px = String(adaptiveSquareMinSizeInput.value || '10');
    }
    if (legacyHighresDraft && scaleInput) {
        payload.enlargement = scaleInput.value;
    }
    return payload;
}

function _applyGeneratePendingUi() {
    prepareLoadingState(0, 0, { expandViewer: true });
    requestWorkflowScroll('viewer', {
        source: 'generate_click',
        pushHistory: true,
        afterFrame: true,
        delayMs: 0,
        behavior: 'auto',
        durationMs: 0,
        stageAnimate: false,
        stageDurationMs: 1,
    });

    const genBtn = document.getElementById('generateBtn');
    if (genBtn) genBtn.disabled = true;
    const collapsedGen = document.getElementById('settingsCollapsedGenerateBtn');
    if (collapsedGen) collapsedGen.disabled = true;

    const resultCard = document.getElementById('resultCard');
    if (resultCard) resultCard.classList.add('is-loading');

    const jobConfig = document.getElementById('jobConfig');
    if (jobConfig) jobConfig.classList.add('hidden');
    const triviaCard = document.getElementById('triviaCard');
    if (triviaCard) triviaCard.classList.add('hidden');

    const progressArea = document.getElementById('progressArea');
    if (progressArea) progressArea.classList.remove('hidden');

    const stopBtn = document.getElementById('stopBtn');
    if (stopBtn) stopBtn.style.display = 'none';

    const bar = document.getElementById('progressBar');
    if (bar) {
        bar.style.width = '0%';
        bar.className = 'progress-fill';
    }
    const stepLabel = document.getElementById('progressStepLabel');
    if (stepLabel) stepLabel.innerText = 'Step 1/4: Analyzing';
    const stepPct = document.getElementById('progressStepPct');
    if (stepPct) stepPct.innerText = '0%';
    const stepDesc = document.getElementById('progressStepDesc');
    if (stepDesc) stepDesc.innerText = 'Waiting for job to start...';
    resetBuildToast();
}

function _rollbackGeneratePendingUi() {
    JobManager.resetResumeUiToIdle();
    openCreateMosaicForEdit({
        forceSettings: true,
        step: 'settings',
        source: 'generate_start_failed',
        pushHistory: false,
    });
}

async function prepareSourceForGeneration() {
    let source = state.selectedSourcePhoto;
    if (!source) {
        return;
    }

    const applyForkData = (forkData) => {
        state.jobId = forkData.new_job_id;
        state.targetJobId = forkData.new_job_id;
        state.targetFilename = forkData.filename;
    };

    const attemptFork = async (photo) => {
        const candidates = [
            String((photo && photo.canonical_parent_job_id) || '').trim(),
            String((photo && photo.upload_job_id) || '').trim(),
            String((photo && photo.last_job_id) || '').trim(),
        ].filter(Boolean);
        const seen = new Set();
        let lastError = null;
        for (const candidate of candidates) {
            if (seen.has(candidate)) continue;
            seen.add(candidate);
            try {
                const forkData = await API.forkJob(candidate, {
                    sourcePhotoId: String((photo && photo.id) || '').trim(),
                });
                if (forkData && forkData.status === 'ok' && forkData.new_job_id) {
                    return forkData;
                }
            } catch (err) {
                lastError = err;
            }
        }
        if (lastError) throw lastError;
        throw new Error('Fork request failed.');
    };

    const refreshSourceFromApi = async (sourceId) => {
        const safeSourceId = String(sourceId || '').trim();
        if (!safeSourceId) return null;
        const refreshed = await API.getSourcePhoto(safeSourceId);
        if (!refreshed || !refreshed.id) return null;
        const normalized = {
            ...refreshed,
            has_been_used: Boolean(refreshed.has_been_used),
        };
        state.selectedSourcePhoto = normalized;
        state.selectedSourcePhotoId = String(normalized.id || state.selectedSourcePhotoId || '');
        state.targetJobId = normalized.upload_job_id || normalized.canonical_parent_job_id || state.targetJobId;
        state.targetFilename = normalized.stored_filename || state.targetFilename;
        const items = Array.isArray(state.sourcePhotos) ? [...state.sourcePhotos] : [];
        const idx = items.findIndex((item) => String((item && item.id) || '') === safeSourceId);
        if (idx >= 0) {
            items[idx] = { ...items[idx], ...normalized };
            state.sourcePhotos = items;
        }
        return normalized;
    };

    if (source.has_been_used) {
        try {
            const forkData = await attemptFork(source);
            applyForkData(forkData);
            return;
        } catch (err) {
            const status = Number((err && err.status) || 0);
            if (!(status === 403 || status === 404 || status === 410) || !source.id) {
                throw err;
            }
            try {
                const refreshed = await refreshSourceFromApi(source.id);
                if (refreshed) {
                    source = refreshed;
                }
            } catch (_refreshErr) {}
            if (source && source.has_been_used) {
                const retryForkData = await attemptFork(source);
                applyForkData(retryForkData);
                return;
            }
        }
    }

    if (!source.upload_job_id) {
        throw new Error('No upload job id available for selected source photo.');
    }
    state.jobId = source.upload_job_id || state.targetJobId || state.jobId;
    state.targetJobId = state.jobId;
    state.targetFilename = source.stored_filename || state.targetFilename;
    source.has_been_used = true;
    state.selectedSourcePhoto = source;
}

export async function startJobController() {
    state.currentJobSimpleDetailExtreme = '';
    if (isWorkflowTransitionLocked()) {
        showToast('One moment', 'Wait for the current transition to finish, then generate.', 2200);
        API.logUiFlow('generate', 'click', 'blocked', { reason: 'workflow_transition_locked' }).catch(() => {});
        return;
    }
    if (!hasTrackedGenerationAttempt) {
        hasTrackedGenerationAttempt = true;
        API.logSiteEvent('app_generation_attempt', {
            page: 'app',
            cta: 'generate',
        }).catch(() => {});
    }

    API.logUiFlow('generate', 'click', 'attempt', {
        has_library: hasSelectedLibraryWithTiles(),
        has_library_selected: Boolean(state.selectedLibraryId),
        has_target_filename: Boolean(state.targetFilename),
        has_source_photo: Boolean(state.selectedSourcePhotoId),
    }).catch(() => {});

    if (!state.selectedSourcePhoto && state.selectedSourcePhotoId) {
        const match = (state.sourcePhotos || []).find((p) => p.id === state.selectedSourcePhotoId);
        if (match) state.selectedSourcePhoto = match;
    }

    if (!state.selectedLibraryId) {
        showToast('Action needed', UI_COPY.alerts.selectLibraryFirst, 2600);
        API.logUiFlow('generate', 'click', 'blocked', { reason: 'missing_library' }).catch(() => {});
        return;
    }
    if (!hasSelectedLibraryWithTiles()) {
        showToast('Action needed', 'Add photos to the selected tile library before generating.', 2600);
        API.logUiFlow('generate', 'click', 'blocked', { reason: 'library_empty' }).catch(() => {});
        return;
    }
    if (!state.targetFilename) {
        showToast('Action needed', UI_COPY.alerts.uploadTargetFirst, 2600);
        API.logUiFlow('generate', 'click', 'blocked', { reason: 'missing_target' }).catch(() => {});
        return;
    }
    if (!state.selectedSourcePhotoId) {
        showToast('Action needed', UI_COPY.alerts.uploadTargetFirst, 2600);
        API.logUiFlow('generate', 'click', 'blocked', { reason: 'missing_source_photo' }).catch(() => {});
        return;
    }

    _applyGeneratePendingUi();

    try {
        await prepareSourceForGeneration();
    } catch (err) {
        console.error("[Main] Failed to prepare source:", err);
        showToast('Could not start job', UI_COPY.alerts.forkFailed, { duration: 0, priority: 'error' });
        _rollbackGeneratePendingUi();
        API.logUiFlow('generate', 'prepare_source', 'error', {
            reason: 'source_prepare_failed',
        }).catch(() => {});
        return;
    }

    state.currentJobSimpleDetailExtreme = getSimpleDetailExtremeEdge();
    if (state.currentJobSimpleDetailExtreme === 'min' || state.currentJobSimpleDetailExtreme === 'max') {
        API.logUiFlow('settings', 'simple_detail_extreme_selected', state.currentJobSimpleDetailExtreme, {
            edge: state.currentJobSimpleDetailExtreme,
        }).catch(() => {});
    }
    const settings = collectJobSettings();
    const started = await JobManager.start(settings, {
        onAccepted: () => {
            const stopBtn = document.getElementById('stopBtn');
            if (stopBtn) stopBtn.style.display = 'inline-block';
            refreshSettingsVisibility();
        }
    });
    if (!started) {
        state.currentJobSimpleDetailExtreme = '';
        _rollbackGeneratePendingUi();
        API.logUiFlow('generate', 'start', 'error').catch(() => {});
        return;
    }
    API.logUiFlow('generate', 'start', 'ok', {
        job_id: state.jobId || '',
    }).catch(() => {});
    API.logSiteEvent('app_generation_started', {
        page: 'app',
        cta: 'generate',
    }).catch(() => {});
    if (!state.isAnonymous) {
        refreshUserConfig();
    }
}

export async function cancelJobController() {
    const ok = await openConfirmModal({
        title: 'Stop current job?',
        message: UI_COPY.jobActions.confirmStop,
        confirmText: 'Stop',
        danger: true,
    });
    if (!ok) return;
    JobManager.cancel();
}
